import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { BadgeCheck, XCircle } from "lucide-react";

export default function AdminVerifications() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/verifications", { params: { status: "pending" } });
      setItems(res.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const review = async (verificationId, decision) => {
    setError("");
    try {
      await api.post(`/admin/verifications/${verificationId}/review`, { decision });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-verifications" className="bg-white rounded-xl border border-slate-200">
      <div className="p-5 border-b border-slate-100 flex items-center gap-2">
        <BadgeCheck className="w-5 h-5 text-brand-turquoise" />
        <h2 className="font-semibold text-brand-navy">Vérifications en attente ({items.length})</h2>
      </div>
      {error && (
        <div data-testid="admin-verifications-error" className="m-5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{error}</div>
      )}
      <div className="divide-y divide-slate-50">
        {items.map((v) => (
          <div key={v.verification_id} data-testid={`admin-verification-${v.verification_id}`} className="px-5 py-4 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-brand-navy">
                {v.type === "skill" ? "Compétence" : "Expérience"} : {v.subject}
              </p>
              <p className="text-xs text-slate-400">
                {v.user_name} · {v.user_email} · {v.created_at ? new Date(v.created_at).toLocaleDateString("fr-CA") : "—"}
              </p>
              {v.evidence && (
                <a href={v.evidence} target="_blank" rel="noopener noreferrer" data-testid={`admin-verification-evidence-${v.verification_id}`} className="text-xs text-brand-blue hover:underline break-all">
                  {v.evidence}
                </a>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                data-testid={`admin-verify-approve-${v.verification_id}`}
                onClick={() => review(v.verification_id, "verified")}
                className="inline-flex items-center gap-1 text-xs font-medium text-green-700 border border-green-300 rounded-lg px-3 py-1.5 hover:bg-green-50"
              >
                <BadgeCheck className="w-3.5 h-3.5" />
                Vérifier
              </button>
              <button
                data-testid={`admin-verify-reject-${v.verification_id}`}
                onClick={() => review(v.verification_id, "rejected")}
                className="inline-flex items-center gap-1 text-xs font-medium text-red-600 border border-red-200 rounded-lg px-3 py-1.5 hover:bg-red-50"
              >
                <XCircle className="w-3.5 h-3.5" />
                Refuser
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p data-testid="admin-verifications-empty" className="px-5 py-8 text-center text-sm text-slate-400">
            Aucune demande de vérification en attente.
          </p>
        )}
      </div>
    </div>
  );
}
